import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { MonthlyReport, Transaction } from '../../types/financial';
import { formatCurrency } from '../../utils/pdfGenerator';
import './Financial.css';

interface FinancialReportsProps {
  onGenerateReport: (month: string) => Promise<MonthlyReport | null>;
  loading?: boolean;
}

export const FinancialReports: React.FC<FinancialReportsProps> = ({
  onGenerateReport,
  loading,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [generating, setGenerating] = useState(false);

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const reportData = await onGenerateReport(selectedMonth);
      setReport(reportData);
    } catch (error: any) {
      alert(error.message || 'Erro ao gerar relatório');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportCSV = () => {
    if (!report) return;

    const csvRows: string[] = [];
    csvRows.push('Data,Descrição,Categoria,Tipo,Valor,Status');
    
    report.transactions.forEach(transaction => {
      const date = new Date(transaction.date).toLocaleDateString('pt-BR');
      csvRows.push(
        `"${date}","${transaction.description}","${transaction.categoryName}","${transaction.type}","${transaction.amount}","${transaction.status}"`
      );
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-financeiro-${selectedMonth}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (!report) return;

    // Criar conteúdo HTML para PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Relatório Financeiro - ${selectedMonth}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1e293b; }
            .summary { background: #f8fafc; padding: 16px; border-radius: 8px; margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
            th { background: #f1f5f9; font-weight: 600; }
            .receita { color: #10b981; }
            .despesa { color: #ef4444; }
          </style>
        </head>
        <body>
          <h1>Relatório Financeiro - ${selectedMonth}</h1>
          <div class="summary">
            <h2>Resumo</h2>
            <p><strong>Receitas:</strong> ${formatCurrency(report.receitas)}</p>
            <p><strong>Despesas:</strong> ${formatCurrency(report.despesas)}</p>
            <p><strong>Saldo Líquido:</strong> ${formatCurrency(report.saldo)}</p>
          </div>
          <h2>Transações</h2>
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Tipo</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${report.transactions.map(t => `
                <tr>
                  <td>${new Date(t.date).toLocaleDateString('pt-BR')}</td>
                  <td>${t.description}</td>
                  <td>${t.categoryName}</td>
                  <td>${t.type === 'receita' ? 'Receita' : 'Despesa'}</td>
                  <td class="${t.type}">${t.type === 'receita' ? '+' : '-'}${formatCurrency(t.amount)}</td>
                  <td>${t.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Card
      title="Relatórios Financeiros"
      actions={
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="ns-input"
            style={{ width: 'auto', padding: '8px' }}
          />
          <Button onClick={handleGenerateReport} disabled={generating || loading}>
            {generating ? 'Gerando...' : 'Gerar Relatório'}
          </Button>
        </div>
      }
    >
      {loading || generating ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Gerando relatório...</p>
        </div>
      ) : report ? (
        <div>
          <div style={{ marginBottom: '24px', display: 'flex', gap: '12px' }}>
            <Button variant="secondary" onClick={handleExportCSV}>
              Exportar CSV
            </Button>
            <Button variant="secondary" onClick={handleExportPDF}>
              Exportar PDF
            </Button>
          </div>

          <div className="ns-financial-report-summary">
            <h3>Resumo do Mês</h3>
            <div className="ns-grid ns-grid-3">
              <div>
                <p className="ns-report-label">Receitas</p>
                <p className="ns-report-value" style={{ color: '#10b981' }}>
                  {formatCurrency(report.receitas)}
                </p>
              </div>
              <div>
                <p className="ns-report-label">Despesas</p>
                <p className="ns-report-value" style={{ color: '#ef4444' }}>
                  {formatCurrency(report.despesas)}
                </p>
              </div>
              <div>
                <p className="ns-report-label">Saldo Líquido</p>
                <p
                  className="ns-report-value"
                  style={{
                    color: report.saldo >= 0 ? '#10b981' : '#ef4444',
                  }}
                >
                  {formatCurrency(report.saldo)}
                </p>
              </div>
            </div>
          </div>

          {report.categoryBreakdown.length > 0 && (
            <div style={{ marginTop: '32px' }}>
              <h3 style={{ marginBottom: '16px' }}>Resumo por Categoria</h3>
              <div className="ns-table-wrapper">
                <table className="ns-table ns-financial-consultations-table">
                  <thead>
                    <tr>
                      <th>Categoria</th>
                      <th>Tipo</th>
                      <th>Total</th>
                      <th>Transações</th>
                      <th>Percentual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.categoryBreakdown.map((category) => (
                      <tr key={category.categoryId}>
                        <td>{category.categoryName}</td>
                        <td>
                          <span
                            className={`ns-financial-type-badge ${
                              category.type === 'receita' ? 'receita' : 'despesa'
                            }`}
                          >
                            {category.type === 'receita' ? 'Receita' : 'Despesa'}
                          </span>
                        </td>
                        <td
                          style={{
                            color: category.type === 'receita' ? '#10b981' : '#ef4444',
                            fontWeight: 600,
                          }}
                        >
                          {formatCurrency(category.total)}
                        </td>
                        <td>{category.count}</td>
                        <td>{category.percentage.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ marginBottom: '16px' }}>Transações ({report.transactions.length})</h3>
            <div className="ns-table-wrapper">
              <table className="ns-table ns-financial-consultations-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>
                        {new Date(transaction.date).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </td>
                      <td>{transaction.description}</td>
                      <td>{transaction.categoryName}</td>
                      <td>
                        <span
                          className={`ns-financial-type-badge ${
                            transaction.type === 'receita' ? 'receita' : 'despesa'
                          }`}
                        >
                          {transaction.type === 'receita' ? 'Receita' : 'Despesa'}
                        </span>
                      </td>
                      <td
                        style={{
                          color: transaction.type === 'receita' ? '#10b981' : '#ef4444',
                          fontWeight: 600,
                        }}
                      >
                        {transaction.type === 'receita' ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td>
                        <span
                          className={`ns-financial-status-badge ${
                            transaction.status === 'pago'
                              ? 'pago'
                              : transaction.status === 'pendente'
                              ? 'pendente'
                              : 'cancelado'
                          }`}
                        >
                          {transaction.status === 'pago'
                            ? 'Pago'
                            : transaction.status === 'pendente'
                            ? 'Pendente'
                            : 'Cancelado'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Selecione um mês e clique em "Gerar Relatório" para visualizar os dados.</p>
        </div>
      )}
    </Card>
  );
};







